// authapi.ts

const serverUrl = import.meta.env.VITE_SERVERURL;
const API_SECRET_TOKEN = import.meta.env.VITE_API_SECRET_TOKEN;

const authHeaders: Record<string, string> = {
  Authorization: `Bearer ${API_SECRET_TOKEN}`,
};

export interface InvoiceData {
  invoiceId: string;
  buyerEmail?: string;
  sellerWallet?: string;
  sellerUsername?: string | null;
  amount: number;
  actualAmount?: number | null;
  token: string;
  chain: string;
  source?: string;
  paymentMethod?: string;
  collectSlug?: string | null;
  dedicatedWalletAddress: string;
  status:
    | "pending"
    | "paid"
    | "completed"
    | "expired"
    | "late_paid"
    | "flagged";
  expiresAt: number | null;
  paidAt?: number | null;
  sweptAt?: number | null;
  sweepTxHash?: string | null;
  incomingTxHash?: string | null;
}

export async function createInvoice(payload: {
  buyerEmail: string;
  amount: number;
  sellerWallet: string;
  sellerUserId?: string;
  sellerUsername?: string;
  source: "request" | "donation" | "buymeCoffee" | "manual";
  paymentMethod: string;
  collectSlug?: string;
}): Promise<InvoiceData> {
  const res = await fetch(`${serverUrl}/api/invoices/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body.error || `Failed to create invoice (${res.status})`);
  }
  return body as InvoiceData;
}

export async function getInvoice(invoiceId: string): Promise<InvoiceData> {
  const res = await fetch(`${serverUrl}/api/invoices/${invoiceId}`, { headers: authHeaders });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body.error || `Failed to load invoice (${res.status})`);
  }
  return body as InvoiceData;
}

export async function listInvoices(params: {
  sellerUserId?: string;
  sellerWallet?: string;
  limit?: number;
}): Promise<InvoiceData[]> {
  const qs = new URLSearchParams();
  if (params.sellerUserId) qs.append("sellerUserId", params.sellerUserId);
  if (params.sellerWallet) qs.append("sellerWallet", params.sellerWallet);
  if (params.limit) qs.append("limit", String(params.limit));
  const res = await fetch(`${serverUrl}/api/invoices?${qs.toString()}`, { headers: authHeaders });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body.error || `Failed to list invoices (${res.status})`);
  }
  return (body.invoices || []) as InvoiceData[];
}

export async function getBalance(walletAddress: string): Promise<string> {
  if (!walletAddress) throw new Error("No wallet address");

  try {
    const response = await fetch(
      `${serverUrl}/get-balance?walletAddress=${walletAddress}`,
      { headers: authHeaders }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Server error: ${error}`);
    }

    const data = await response.json();
    return data.balance || "0";
  } catch (err: any) {
    console.error("Error fetching balance:", err);
    throw new Error("Failed to fetch balance: " + err.message);
  }
}

export interface AvailableBalance {
  /** Total on-chain USDC. */
  balance: string;
  /**
   * Always "0" — Bridge deposits are now held in CashTap custody wallets
   * until an admin approves them and never appear in the wallet balance.
   * Kept for shape stability.
   */
  held: string;
  /** Equal to `balance`: everything on-chain is spendable. */
  available: string;
}

/**
 * Spendable balance. Bridge onramp deposits are held in custody wallets until
 * admin approval and never appear in the wallet balance, so `available` always
 * equals `balance`. Falls back to treating the full balance as available if
 * the endpoint is unreachable — a balance read failure must not strand a user.
 */
export async function getAvailableBalance(
  walletAddress: string,
): Promise<AvailableBalance> {
  if (!walletAddress) throw new Error("No wallet address");

  const response = await fetch(
    `${serverUrl}/get-available-balance?walletAddress=${walletAddress}`,
    { headers: authHeaders },
  );

  if (!response.ok) {
    throw new Error(`Server error: ${await response.text()}`);
  }

  const data = await response.json();
  return {
    balance: data.balance ?? "0",
    held: data.held ?? "0",
    available: data.available ?? data.balance ?? "0",
  };
}

export async function getRecentTransactions(walletAddress: string): Promise<
  {
    id: string;
    type: "sent" | "received";
    amount: number;
    from: string;
    to: string;
    date: string;
    hash: string;
    senderEmail?: string | null;
    paymentMethod?: string | null;
    sourceLabel?: string | null;
    invoiceId?: string | null;
    invalid?: boolean;
    invalidReason?: string | null;
    explorerLink?: string | null;
  }[]
> {
  if (!walletAddress) throw new Error("No wallet address");

  try {
    const response = await fetch(
      `${serverUrl}/get-transactions?walletAddress=${walletAddress}`,
      { headers: authHeaders }
    );

    if (!response.ok) {
      console.warn("Transaction fetch failed, returning empty list");
      return [];
    }

    const data = await response.json();
    return (data.transactions || []).map((tx: any) => ({
      ...tx,
      date: tx.date && !isNaN(new Date(tx.date).getTime())
        ? new Date(tx.date).toLocaleString()
        : "--",
    }));
  } catch (err: any) {
    console.error("Error fetching transactions:", err);
    return []; // Fail gracefully
  }
}

export interface CoinbaseOrder {
  orderId: string;
  amount: number;
  status: "pending" | "in_progress" | "completed" | "failed" | "initiated" | string;
  paymentMethod: string | null;
  mode: "embed" | "redirect" | string;
  paymentLink: string | null;
  txHash: string | null;
  createdAt: string | null;
}

// List the buyer's recent Coinbase onramp orders for the Add Funds tracker.
export async function getCoinbaseOrders(
  walletAddress: string,
): Promise<CoinbaseOrder[]> {
  if (!walletAddress) return [];
  try {
    const response = await fetch(
      `${serverUrl}/coinbase-orders?walletAddress=${walletAddress}`,
      { headers: authHeaders },
    );
    if (!response.ok) return [];
    const data = await response.json();
    return (data.orders || []) as CoinbaseOrder[];
  } catch (err) {
    console.error("Error fetching Coinbase orders:", err);
    return [];
  }
}

// Refresh one order's status from Coinbase (also persists it server-side).
export async function refreshCoinbaseOrderStatus(
  orderId: string,
): Promise<{ status: string; txHash: string | null } | null> {
  if (!orderId) return null;
  try {
    const response = await fetch(
      `${serverUrl}/coinbase-order-status/${encodeURIComponent(orderId)}`,
      { headers: authHeaders },
    );
    if (!response.ok) return null;
    const data = await response.json();
    return { status: data.status, txHash: data.txHash || null };
  } catch (err) {
    console.error("Error refreshing Coinbase order status:", err);
    return null;
  }
}

// Type definitions for verified transactions
export interface VerificationDetails {
  foundInDb: boolean;
  foundOnChain: boolean | null;
  hashMatch?: boolean;
  amountMatch?: boolean;
  discrepancies?: string[];
  reason?: string;
}

export interface VerifiedTransaction {
  id: string;
  type: "sent" | "received";
  amount: number;
  from: string;
  to: string;
  date: string;
  hash: string;
  verificationStatus: "verified" | "db_only" | "chain_only" | "unverified";
  verificationDetails: VerificationDetails;
  senderEmail?: string | null;
  paymentMethod?: string | null;
  sourceLabel?: string | null;
  invoiceId?: string | null;
  chainData?: any;
}

export interface VerificationSummary {
  total: number;
  verified: number;
  dbOnly: number;
  chainOnly: number;
  verificationRate: string;
}

export interface VerifiedTransactionsResponse {
  transactions: VerifiedTransaction[];
  summary: VerificationSummary;
  metadata: {
    fromDate: string;
    toDate: string;
    queriedAt: string;
    baseScanAvailable: boolean;
    error?: string;
  };
}

/**
 * Fetch transactions with on-chain verification from BaseScan
 */
export async function getTransactionsVerified(
  walletAddress: string,
  fromDate: Date,
  toDate: Date,
): Promise<VerifiedTransaction[]> {
  if (!walletAddress) throw new Error("No wallet address");

  try {
    // Fetch all transactions - frontend will filter by date
    const response = await fetch(
      `${serverUrl}/get-transactions-verified?walletAddress=${walletAddress}`,
      { headers: authHeaders }
    );

    if (!response.ok) {
      console.warn(
        `Transaction fetch failed with status ${response.status}`
      );
      return [];
    }

    const data = await response.json();
    const allTransactions = (data.transactions || []);

    // Filter by date range on the frontend
    const filtered = allTransactions.filter((tx: any) => {
      const txDate = tx.date ? new Date(tx.date) : null;
      if (!txDate || isNaN(txDate.getTime())) return false;
      return txDate >= fromDate && txDate <= toDate;
    });

    // Normalize dates to locale string format for display
    return filtered.map((tx: any) => ({
      ...tx,
      type: tx.type || "unknown",
      amount: typeof tx.amount === "number" ? tx.amount : 0,
      date: tx.date && !isNaN(new Date(tx.date).getTime())
        ? new Date(tx.date).toLocaleString()
        : "--",
    }));
  } catch (err: any) {
    console.error("Error fetching verified transactions:", err);
    return [];
  }
}

/**
 * Fetch verification summary for a date range
 */
export async function getTransactionVerificationSummary(
  walletAddress: string,
  fromDate: Date,
  toDate: Date,
): Promise<VerificationSummary | null> {
  if (!walletAddress) return null;

  try {
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const fromDateStr = formatDate(fromDate);
    const toDateStr = formatDate(toDate);

    const response = await fetch(
      `${serverUrl}/get-transactions-verified?walletAddress=${walletAddress}&fromDate=${fromDateStr}&toDate=${toDateStr}`,
      { headers: authHeaders }
    );

    if (!response.ok) return null;

    const data: VerifiedTransactionsResponse = await response.json();
    return data.summary || null;
  } catch (err) {
    console.error("Error fetching verification summary:", err);
    return null;
  }
}
